import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import ComparisonTable from "../components/ComparisonTable";
import TrackWeekSelector from "../components/TrackWeekSelector";
import { useComparisonStore } from "../state/comparisonStore";
import { useRunStore } from "../state/runStore";
import { useTeamStore } from "../state/teamStore";

const SESSION_TYPES = ["Test", "Practice", "Qualifying", "Race"] as const;
type SessionType = (typeof SESSION_TYPES)[number];

const SESSION_COLORS: Record<SessionType, string> = {
  Test: "var(--flag-purple)",
  Practice: "var(--live)",
  Qualifying: "var(--flag-orange)",
  Race: "var(--flag-red)",
};

export default function MasterComparison() {
  const { trackWeekId: routeTrackWeekId } = useParams<{ trackWeekId: string }>();
  const { activeTeamId, activeTeam } = useTeamStore();
  const { selectedTrackWeekId, fetchTrackWeeks } = useRunStore();
  const { rankings, loading, fetchMasterRankings } = useComparisonStore();
  const [activeFilters, setActiveFilters] = useState<Set<SessionType>>(new Set(["Test", "Practice", "Qualifying"]));

  const toggleFilter = (type: SessionType) => {
    setActiveFilters((prev) => {
      const next = new Set(prev);
      if (next.has(type)) {
        if (next.size > 1) next.delete(type);
      } else {
        next.add(type);
      }
      return next;
    });
  };

  const eventTypesParam = Array.from(activeFilters).join(",");

  useEffect(() => {
    if (activeTeamId) {
      void fetchTrackWeeks(activeTeamId);
    }
  }, [activeTeamId, fetchTrackWeeks]);

  useEffect(() => {
    if (activeTeamId && selectedTrackWeekId) {
      void fetchMasterRankings(activeTeamId, selectedTrackWeekId, "top10_avg", eventTypesParam);
    }
  }, [activeTeamId, selectedTrackWeekId, eventTypesParam, fetchMasterRankings]);

  const handleSort = (field: string) => {
    if (activeTeamId && selectedTrackWeekId) {
      void fetchMasterRankings(activeTeamId, selectedTrackWeekId, field, eventTypesParam);
    }
  };

  return (
    <div>
      {/* Tab content under the WeekWorkspace hero — controls only, no page title */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "0.75rem", flexWrap: "wrap", marginBottom: "1rem" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "0.4rem", flexWrap: "wrap" }}>
        {SESSION_TYPES.map((type) => {
          const active = activeFilters.has(type);
          return (
            <button
              key={type}
              onClick={() => toggleFilter(type)}
              style={{
                padding: "0.3rem 0.6rem",
                borderRadius: 6,
                border: `1px solid ${active ? `color-mix(in srgb, ${SESSION_COLORS[type]} 38%, transparent)` : "var(--rule-faint)"}`,
                background: active ? `color-mix(in srgb, ${SESSION_COLORS[type]} 12%, transparent)` : "transparent",
                color: active ? SESSION_COLORS[type] : "var(--text-muted)",
                fontFamily: "var(--font-body)",
                fontSize: "0.8125rem",
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              {type}
            </button>
          );
        })}
        </div>
        <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
          {activeTeam && <span className="pill pill--accent">{activeTeam.name}</span>}
          <span className="pill">{rankings.length} ranked</span>
        </div>
      </div>

      {!routeTrackWeekId && (
        <div style={{ marginBottom: "1rem" }}>
          <TrackWeekSelector />
        </div>
      )}

      {loading ? (
        <div className="empty-state">
          <div className="spinner spinner-lg" />
          <div className="empty-state-title">Loading rankings</div>
          <div className="empty-state-text">Building the master board for the selected week.</div>
        </div>
      ) : !selectedTrackWeekId ? (
        <div className="empty-state">
          <div className="empty-state-title">Choose a week</div>
          <div className="empty-state-text">Select a track week first to load the master comparison board.</div>
        </div>
      ) : rankings.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-title">No ranked runs yet</div>
          <div className="empty-state-text">Once the team uploads runs for this week, the ranking board will appear here.</div>
        </div>
      ) : (
        <div className="card xpe-table-scroll" style={{ padding: 0, overflow: "hidden" }}>
          <div style={styles.tableHeader}>
            <div>
              <div className="section-kicker" style={{ marginBottom: "0.35rem" }}>Rankings</div>
              <div style={styles.tableTitle}>Driver leaderboard</div>
            </div>
            <span className="pill">{rankings.length} entries</span>
          </div>
          <ComparisonTable rankings={rankings} onSort={handleSort} />
        </div>
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  tableHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: "1rem",
    padding: "1rem 1.1rem",
    borderBottom: "1px solid var(--rule-strong)",
    background: "var(--ink-1)",
    flexWrap: "wrap",
  },
  tableTitle: {
    fontFamily: "var(--font-display)",
    fontSize: "1.15rem",
    fontWeight: 700,
    color: "var(--text-strong)",
  },
};
